import { get, uniq } from "lodash";
import { useMemo } from "react";
import format from "string-template";
import { useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { resolveProductName } from "utils/helpers/resolveProductName";
import Card from "./Card";
import { QUOTE_ATTACHMENT_ORDER } from "utils/constants";

const ACORIS_LOGO_URL =
  "https://acorismutuelles.fr/wp-content/uploads/2025/11/Logo-acoris-mutuelle.svg";
const PAPERCLIP_ICON_URL =
  "https://img.icons8.com/material-rounded/24/eb6739/attach.png";

const EmailTemplate = ({
  civility,
  lastName,
  firstName,
  adminPhoneNumber,
  adminEmail,
  adminFirstName,
  adminLastName,
  translation,
  costs,
  addLinks,
  organizationId,
  attachments,
  project,
}) => {
  const productCodes = useMemo(
    () =>
      uniq(
        (costs || [])
          .map((cost) => get(cost, "insurancePlan.product"))
          .filter(Boolean),
      ),
    [costs],
  );

  const shouldResolveProductName = productCodes.length > 0 && !!organizationId;

  const { data: productRangesData, loading: productRangesLoading } = useQuery(
    graphql.queries.PRODUCT_RANGES,
    {
      variables: {
        where: { ownedBy: { id: organizationId } },
        take: 500,
        orderBy: [{ code: "ASC" }],
      },
      skip: !shouldResolveProductName,
    },
  );

  const isProductNameReady = !shouldResolveProductName || !productRangesLoading;

  const offerName = useMemo(() => {
    if (!isProductNameReady) return "";

    return resolveProductName({
      productCodes,
      productRanges: get(productRangesData, "productRanges.data", []),
    });
  }, [isProductNameReady, productCodes, productRangesData]);

  const sortedAttachments = useMemo(
    () =>
      attachments.map((attachment) => {
        return {
          ...attachment,
          order: QUOTE_ATTACHMENT_ORDER[attachment.type],
        };
      }),
    [attachments],
  );

  const removeDuplicates = (array, property = "name") => {
    const seen = new Set();
    return array.filter((item) => {
      const value = String(item[property] || "");
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  };

  const { documentsTitle, documentNameByType, documents } = useMemo(() => {
    const block = get(translation, "ATTACHED_DOCUMENTS.DEFAULT", {});
    // const items = get(block, "ITEMS", []);
    const documentNameByType = get(block, "DOCUMENT_NAME_BY_TYPE", {});
    const items = removeDuplicates(
      sortedAttachments.map((_) => ({
        ..._,
        name: get(documentNameByType, _.type, _.name),
      })),
    );

    return {
      documentsTitle: get(block, "TITLE", ""),
      documents: items,
      // documentNameByType,
    };
  }, [translation]);

  const documentsMidpoint = Math.ceil(documents.length / 2);
  const leftDocuments = documents.slice(0, documentsMidpoint);
  const rightDocuments = documents.slice(documentsMidpoint);

  return (
    <div className="quote-template-acoris__main-container">
      <div className="quote-template-acoris__header">
        <img src={ACORIS_LOGO_URL} alt="Acoris Mutuelles" />
      </div>
      <div className="quote-template-acoris__container">
        <div className="quote-template-acoris__content">
          <p>
            {translation.HELLO}{" "}
            <strong>
              {translation[civility] || civility} {firstName} {lastName}
            </strong>
            ,
          </p>
          {isProductNameReady ? (
            <p
              className="quote-template-acoris__content--highlight"
              dangerouslySetInnerHTML={{
                __html: format(translation.P1, { offerName }),
              }}
            />
          ) : null}
        </div>
        <div className="quote-template-acoris__formulas">
          {costs?.map(({ id, insurancePlan, cost, currency }, index) => (
            <Card
              key={id}
              insurancePlan={insurancePlan}
              cost={cost}
              currency={currency}
              translation={translation}
              index={index}
              addLinks={addLinks}
              upsells={get(
                project,
                `fields.upsells.${get(insurancePlan, "id")}`,
                [],
              )}
            />
          ))}
        </div>
        <div className="quote-template-acoris__content">
          <p className="quote-template-acoris__documents-title">
            <strong>{documentsTitle}</strong>
          </p>
        </div>
        <div className="quote-template-acoris__documents">
          <div className="quote-template-acoris__documents--column">
            {leftDocuments.map((doc, index) => (
              <div key={index} className="quote-template-acoris__document">
                <img
                  className="quote-template-acoris__document--icon"
                  src={PAPERCLIP_ICON_URL}
                  alt=""
                />
                <span className="quote-template-acoris__document--link">
                  {get(doc, "name")}
                </span>
              </div>
            ))}
          </div>
          <div className="quote-template-acoris__documents--column">
            {rightDocuments.map((doc, index) => (
              <div
                key={documentsMidpoint + index}
                className="quote-template-acoris__document"
              >
                <img
                  className="quote-template-acoris__document--icon"
                  src={PAPERCLIP_ICON_URL}
                  alt=""
                />
                <span className="quote-template-acoris__document--link">
                  {get(doc, "name")}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="quote-template-acoris__content">
          <p>{translation.TERMINATION_INFO}</p>
        </div>
        <div className="quote-template-acoris__content quote-template-acoris__content--contact">
          <p
            dangerouslySetInnerHTML={{
              __html: format(translation.CONTACT_INFO, {
                phoneNumber: adminPhoneNumber,
                email: adminEmail,
              }),
            }}
          />
        </div>
        <div className="quote-template-acoris__content">
          <p>{translation.CLOSING}</p>
        </div>
        <div className="quote-template-acoris__info">
          <p className="quote-template-acoris__info--label">
            {translation.YOUR_ADVISOR}
          </p>
          <p className="quote-template-acoris__info--name">
            {adminFirstName} {adminLastName}
          </p>
          <p>{adminPhoneNumber}</p>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplate;
